import { SkillExecutionDetails } from './SkillExecutionDetails.js';
import { Link } from '@tanstack/react-router';
import {
  CheckCircle2Icon,
  CircleAlertIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  Loader2Icon,
  MoreVerticalIcon,
  PencilIcon,
  PlayCircleIcon,
  SparklesIcon,
  ZapIcon,
} from 'lucide-react';
import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Card } from '@/components/ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { artifactUrl } from '@/lib/api/quality.js';
import type {
  ApiCallSummary,
  DocumentEvidenceSummary,
  ApplicationExchange,
  CaseAttachment,
  CaseAttempt,
  CaseDetail,
  CaseReplay,
  ReapedReason,
  ReplayStepStatus,
  TestResult,
  TestSource,
} from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

import {
  elapsedSinceStart,
  formatElapsed,
  isUsableStart,
  useTickingNow,
} from '../elapsed.js';
import { isReplayObservation } from '../replay-observation.js';
import {
  isTextToolCallObservation,
  TEXT_TOOL_CALLS_EXPLANATION,
} from '../tool-call-observation.js';
import { AuthImage } from '../AuthMedia.js';
import {
  OutcomeReassessmentDialog,
  useOutcomeReassessment,
} from '../OutcomeReassessment.js';
import type { CorrectableOutcome } from '../OutcomeCorrectionDialog.js';
import {
  assessingTooltip,
  caseDisplayStatus,
  caseOutcome,
  isOutcomeReassessmentCandidate,
  hasAppliedOutcomeReassessment,
  outcomeTooltip,
  requiresOutcomeReview,
} from '../quality-outcome.js';
import {
  AnswerJudgementBadges,
  AnswerJudgementDetails,
  readAnswerJudgement,
} from './AnswerJudgement.js';
import { ApiCallEvidence } from './ApiCallEvidence.js';
import { DocumentEvidence } from './DocumentEvidence.js';
import { AppExchangeEvidence } from './AppExchangeEvidence.js';
import {
  caseReasonText,
  deviationCount,
  formatDate,
  formatDuration,
  formatTimecode,
  platformComponentIcon,
  StatusBadge,
} from '../quality-presentation.js';
import type { StepSeekHandler } from '../player/recordings.js';
import {
  CapturedVariables,
  capturedVariableEntries,
} from './CapturedVariables.js';

/** Human text for a compiler non-cacheable reason code. API-cast: an unknown code (a newer service)
 * falls back to a humanized form of the raw code rather than blank. */
const NON_CACHEABLE_REASONS: Record<string, string> = {
  no_replayable_actions: 'no step produced a replayable action',
  precondition_not_satisfied: 'the login / precondition never completed',
  unattributed_precondition_credential:
    'the login credential could not be attributed',
  precondition_not_replayable:
    'a login / precondition step could not be turned into a durable replay action',
  credential_resolve_failed: 'the login credential could not be resolved',
  secret_leak_risk: 'replaying it would require storing a secret',
  positional_xpath_locator: 'a brittle positional XPath locator',
  unparsable_playwright_code: 'the recorded action could not be parsed',
  unparsable_drag_target: 'the drag target could not be resolved to an element',
  capture_ref_unbound:
    'a request uses a value saved by a step that cannot be replayed',
};

/** Shortens a device udid to its first 8 characters for the row subtitle; the full value is always
 *  available on hover via the element's `title`. */
function truncateUdid(udid: string): string {
  return udid.length > 8 ? `${udid.slice(0, 8)}…` : udid;
}

/** Turns a raw reason code into readable text. Unknown codes (a newer server, or an
 * `unsupported_action_<verb>` the map doesn't list) fall back to a de-underscored form. */
function humanizeReason(code: string): string {
  // Own-property guard: an API-supplied code like `__proto__` must not resolve up the prototype
  // chain to a non-string. Fall back to a de-underscored form of the raw code.
  const known = Object.prototype.hasOwnProperty.call(
    NON_CACHEABLE_REASONS,
    code,
  )
    ? NON_CACHEABLE_REASONS[code]
    : undefined;
  return known ?? code.replace(/_/g, ' ');
}

/** Plain-language explanation for a triage marker. The marker is metadata about reviewability;
 * it must not change the case's underlying status or imply that the outcome itself is provisional. */
function triageTooltip(reason?: unknown): string {
  if (reason === 'cleanup_unconfirmed') {
    return 'Cleanup of the test environment could not be confirmed, so this case needs review.';
  }
  return typeof reason === 'string' && reason
    ? `This case needs review: ${reason.replace(/_/g, ' ')}.`
    : 'This case needs review.';
}

/** True when this case cached. The web panel is a cache-hit view: it shows only cached steps, so a
 * run with nothing cached hides the panel entirely. A genuine cache hit still shows even when the
 * server omits the per-step breakdown (`steps` is absent on older builds) — `cached` / `replayed`
 * carry the status lines on their own; only require a cached step when neither says it's a hit. */
function hasCachingInfo(replay?: CaseReplay | null): replay is CaseReplay {
  if (!replay) return false;
  const cacheHit = replay.cached === true || replay.mode === 'replayed';
  const hasCachedStep =
    Array.isArray(replay.steps) && replay.steps.some((step) => step.cached);
  return cacheHit || hasCachedStep;
}

/** How this run was cached, as one plain-language line. Exported for tests. */
export function thisRunLine(replay: CaseReplay, costRecorded = true): string {
  if (replay.mode === 'replayed') {
    const steps =
      replay.step_count != null
        ? `${replay.step_count} step${replay.step_count === 1 ? '' : 's'} `
        : '';
    const costText = costRecorded ? 'no LLM, $0' : 'no LLM; cost not recorded';
    return `This run: cached — replayed ${steps}deterministically from a recorded script (${costText}).`;
  }
  if (replay.mode === 'healed') {
    const at =
      replay.healed_step != null ? ` at step ${replay.healed_step}` : '';
    return `This run: the recorded replay went stale${at}, so the LLM reran and re-recorded it.`;
  }
  if (replay.mode === 'llm') return 'This run: ran via the LLM.';
  return 'This run: execution mode not recorded.';
}

/** The cacheability status of the case's current definition, as one plain-language line. Exported for tests. */
export function cacheStatusLine(replay: CaseReplay): string {
  if (replay.cacheable === 'active') {
    const steps =
      replay.step_count != null
        ? `${replay.step_count} step${replay.step_count === 1 ? '' : 's'}`
        : 'on file';
    return `Replay script ${steps} — future runs replay this case without the LLM.`;
  }
  if (replay.cacheable === 'non_cacheable') {
    return `Not cacheable — ${replay.non_cacheable_reason ? humanizeReason(replay.non_cacheable_reason) : 'the compiler could not build a replay script'}.`;
  }
  if (replay.cacheable === 'superseded') {
    return 'The recorded script is stale (the case definition changed); it will re-record on the next run.';
  }
  if (replay.cacheable === 'none') return 'No replay script recorded yet.';
  // API-cast: a newer server could send a cacheability state this build doesn't know.
  return 'Replay status unavailable.';
}

/** Display label for one step in the caching list: its text (or "Step N"), plus the humanized reason
 * for a not-cached step when the compiler recorded one. */
export function stepStatusLabel(step: ReplayStepStatus): string {
  const name = step.step_text ?? `Step ${step.step_index}`;
  return !step.cached && step.reason
    ? `${name} — ${humanizeReason(step.reason)}`
    : name;
}

/** One cached step in the caching list. When it carries actions it's a disclosure: click to reveal
 * the individual actions (click/type/navigate/…) it replays. (The web panel lists cached steps
 * only.) */
function CachingStepRow({ step }: { step: ReplayStepStatus }): JSX.Element {
  const [open, setOpen] = useState(false);
  // API-cast: tolerate a missing/non-array `actions` rather than throwing on `.length`/`.map`.
  const actions = Array.isArray(step.actions) ? step.actions : [];
  const expandable = actions.length > 0;
  return (
    <li className="text-xs text-muted-foreground">
      <div
        className={cn(
          'flex items-start gap-1.5',
          expandable && 'cursor-pointer',
        )}
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
        role={expandable ? 'button' : undefined}
        aria-expanded={expandable ? open : undefined}
        tabIndex={expandable ? 0 : undefined}
        onKeyDown={(event) => {
          if (expandable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <CheckCircle2Icon
          className="mt-0.5 size-3 shrink-0 text-success-500"
          aria-hidden
        />
        <span className="flex-1">Cached: {stepStatusLabel(step)}</span>
        {expandable ? (
          open ? (
            <ChevronDownIcon className="mt-0.5 size-3 shrink-0" aria-hidden />
          ) : (
            <ChevronRightIcon className="mt-0.5 size-3 shrink-0" aria-hidden />
          )
        ) : null}
      </div>
      {expandable && open ? (
        <ol className="ml-5 mt-0.5 list-decimal space-y-0.5 pl-3 text-muted-foreground/80">
          {actions.map((action, i) => (
            <li key={i}>{action}</li>
          ))}
        </ol>
      ) : null}
    </li>
  );
}

/** A plain-language "Caching" log for one case: how this run executed (deterministic replay, healed,
 * or LLM), the cacheability of the case going forward, and the list of steps that cached (each
 * expands to its actions). Non-cached steps are omitted here — the panel is a cache-hit view.
 * `omitForwardLooking` drops `cacheStatusLine`'s "future runs replay this case…" line (F14): under a
 * failed case that forward-looking line reads as "it used a cache, and that is why it failed" —
 * `thisRunLine` already says what happened on THIS run, which is what belongs next to a failure. */
function CachingDetail({
  replay,
  costRecorded,
  omitForwardLooking = false,
  usageLine = null,
}: {
  replay: CaseReplay;
  costRecorded?: boolean;
  omitForwardLooking?: boolean;
  /** `cacheUsageLine` for the case's steps, shown under the run line: which steps the cache covered. */
  usageLine?: string | null;
}): JSX.Element {
  const runLines = [
    thisRunLine(replay, costRecorded),
    ...(usageLine ? [usageLine] : []),
  ];
  const lines = omitForwardLooking
    ? runLines
    : [...runLines, cacheStatusLine(replay)];
  // API-cast: tolerate a missing/non-array `steps` rather than throwing on `.length`/`.map`.
  const steps = (Array.isArray(replay.steps) ? replay.steps : []).filter(
    (step) => step.cached,
  );
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-2">
      <div
        className="flex cursor-pointer items-center gap-1.5"
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        {open ? (
          <ChevronDownIcon
            className="size-3.5 text-muted-foreground"
            aria-hidden
          />
        ) : (
          <ChevronRightIcon
            className="size-3.5 text-muted-foreground"
            aria-hidden
          />
        )}
        <ZapIcon
          className={cn(
            'size-3.5',
            replay.mode === 'replayed'
              ? 'text-info-500'
              : 'text-muted-foreground',
          )}
          aria-hidden
        />
        <span className="text-caption font-medium text-foreground">
          Caching
        </span>
      </div>
      {open ? (
        <div className="mt-1">
          {lines.map((line, i) => (
            <p key={i} className="text-xs text-muted-foreground">
              {line}
            </p>
          ))}
          {steps.length > 0 ? (
            <ul className="mt-1.5 space-y-0.5">
              {steps.map((step) => (
                <CachingStepRow key={step.step_index} step={step} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function statusDot(status: string): string {
  if (status === 'passed') return 'bg-success-500';
  if (status === 'timeout') return 'bg-warning-500';
  if (status === 'failed' || status === 'broken') return 'bg-destructive';
  if (status === 'running' || status === 'in_progress') return 'bg-info-500';
  return 'bg-muted-foreground/40';
}

/** The step currently executing rides as `in_progress` (or `running`) in the live step stream. */
function isRunningStep(status: string): boolean {
  return status === 'in_progress' || status === 'running';
}

/** A case in any of these states is finished — no step under it should ever still spin. */
const TERMINAL_CASE_STATUSES = new Set([
  'passed',
  'failed',
  'broken',
  'timeout',
  'cancelled',
  'defect_found',
  'case_unclear',
  'blocked_on_input',
  'qa_error',
  'infra_error',
]);

/** A case verdict worth pinning up front (S10, AC 24/25) — a case merely `cancelled` has no failure
 *  content of its own to lead with. */
const FAILURE_STATUSES = new Set([
  'failed',
  'broken',
  'timeout',
  'defect_found',
  'case_unclear',
  'blocked_on_input',
  'qa_error',
  'infra_error',
]);

/** The last substantive failure step before unexecuted steps; otherwise the last non-passed step in `steps`, or -1 when every step passed (or there are none). This is
 *  what gets pinned to the top of a failure-first expansion — the step closest to what actually went
 *  wrong, not necessarily the last step overall. */
function findPinnedStepIndex(steps: RenderStep[]): number {
  const failure = new Set([
    'failed',
    'blocked',
    'broken',
    'timeout',
    'error',
    ...FAILURE_STATUSES,
  ]);
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    if (failure.has(steps[index]?.status ?? '')) return index;
  }
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (step && step.status !== 'passed') return index;
  }
  return -1;
}

/**
 * Plain-language explanation for this case's outcome (R3): what the judge decided when a judge ran,
 * or — for the far larger cluster of deterministically classified cases a judge never reviewed — the
 * failing step's own observation, the only human-readable text that exists for them. `fromJudge`
 * tells the caller which one it got so the row can say so rather than presenting a step trace as if
 * it were an AI verdict. Returns null when neither exists; never fabricates a sentence.
 *
 * Reads `rationale` with `||`, not `??`: a stored empty-string rationale must still fall through to
 * the judge's own sub-object, then to the step observation, the same empty-string idiom this estate
 * uses elsewhere. Each candidate is trimmed BEFORE the fallback, not after — trimming the winner
 * would let a whitespace-only value win the `||`, block the next candidate, and then render as
 * nothing. The observation is trimmed for the same reason. An observation that is only tool calls a
 * model wrote as text is replaced by a plain explanation (MEDA-780): the raw JSON says nothing to a
 * reader, and the step row below still shows it as evidence.
 */
function caseRationale(
  result: TestResult,
  failingStep: RenderStep | undefined,
): { text: string; fromJudge: boolean } | null {
  const judgeRationale =
    result.classification?.rationale?.trim() ||
    result.classification?.judge?.rationale?.trim();
  if (judgeRationale) return { text: judgeRationale, fromJudge: true };
  const observation = failingStep?.observation?.trim();
  if (!observation || isReplayObservation(observation)) return null;
  if (isTextToolCallObservation(observation))
    return { text: TEXT_TOOL_CALLS_EXPLANATION, fromJudge: false };
  return { text: observation, fromJudge: false };
}

/** Defensive guard: a step only renders as "running" (spinner) while its PARENT case is still live.
 *  Service-side terminalization (cancel/reaper) clamps the streamed in_progress step, but a stale
 *  step from any path — or an old run persisted before that clamp — must never spin under a terminal
 *  case. `caseStatus` undefined (per-attempt blocks have no own status) → trust the step's own status. */
function isLiveRunningStep(stepStatus: string, caseStatus?: string): boolean {
  if (caseStatus && TERMINAL_CASE_STATUSES.has(caseStatus)) return false;
  return isRunningStep(stepStatus);
}

/** The live step list prepends preconditions, named "Precondition: …" — exclude them from the count. */
function isPreconditionStep(name: string): boolean {
  return name.startsWith('Precondition:');
}

/** Locate the step the executor is on right now, for the running-row subtitle. `n` is its 1-based
 *  position among real steps reached so far; `total` is the planned step count from the case spec
 *  (0 when unknown → the UI shows "step N" without a denominator). Null when no step is in progress. */
function liveCurrentStep(
  steps: RenderStep[],
  spec: unknown,
): { n: number; total: number; name: string } | null {
  let reached = 0;
  for (const s of steps) {
    if (isPreconditionStep(s.name)) continue;
    reached += 1;
    if (isRunningStep(s.status)) {
      const planned = (spec as { test_steps?: unknown[] } | null | undefined)
        ?.test_steps;
      return {
        n: reached,
        total: Array.isArray(planned) ? planned.length : 0,
        name: s.name || 'step',
      };
    }
  }
  return null;
}

function Screenshot({
  runId,
  attachment,
}: {
  runId: string;
  attachment: CaseAttachment;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const src = artifactUrl(runId, attachment.key);
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="block overflow-hidden rounded-md border border-border transition-colors hover:border-foreground/30"
      title={attachment.name}
    >
      <AuthImage
        src={src}
        alt={attachment.name}
        className={cn(
          'object-cover',
          open ? 'max-h-[70vh] w-auto' : 'h-20 w-28',
        )}
      />
    </button>
  );
}

/** A normalized step for rendering — live steps lack observation/attachments, report steps have them. */
interface RenderStep {
  name: string;
  status: string;
  /** Zero-based authored test-step index; the login step has none. Matches a result step. */
  step_index?: number | null;
  /** How the step ran this time, from the result step (MEDA-1094). Absent on a run without replay. */
  executionMode?: StepExecutionMode;
  observation?: string;
  attachments?: CaseAttachment[];
  duration_ms?: number | string | null;
  /** Epoch ms the step began, streamed on the live `progress.json` entry; absent on finished
   *  fragments and on runs that predate the field. Drives the running step's live elapsed time. */
  start?: number | string | null;
  timecode_ms?: number | null;
  /** The recording `timecode_ms` is measured in, when the case recorded several files. */
  video_key?: string;
  /** Failed application HTTP exchanges bound to this step's window; see `ApplicationExchange`. */
  app_exchanges?: ApplicationExchange[];
  /** The API calls this step made; see `ApiCallSummary`. */
  api_calls?: ApiCallSummary[];
  /** The PDFs this step read or rendered; see `DocumentEvidenceSummary`. */
  documents?: DocumentEvidenceSummary[];
  /** See `CaseStepDetail.evidence_status`. */
  evidence_status?: 'captured' | 'unavailable' | 'not_applicable';
  /** The component this step ran on ('web' | 'mobile'); drives the platform badge + which recording
   *  a seek targets. Absent on single-component runs / older reports → no badge. */
  component?: string;
  /** Wave 2: the expectation vs what was observed, and whether the service ruled the mismatch
   *  cosmetic. See `CaseStepDetail` in quality-types.ts. */
  expected_text?: string | null;
  actual_text?: string | null;
  deviation?: boolean;
}

/**
 * A live step's numeric-ish field, or undefined when it is neither a number nor a string.
 *
 * The worker's live steps ride through the service as `Record<string, unknown>` (deliberately — a
 * DTO would whitelist the keys away), so these values are genuinely untyped on arrival. Everything
 * downstream already validates the value itself; this only refuses shapes that have no business
 * being there, so an object or an array cannot reach a formatter.
 */
function asNumberOrString(value: unknown): number | string | undefined {
  return typeof value === 'number' || typeof value === 'string'
    ? value
    : undefined;
}

/** Human label for the component a step ran on; null when absent/unknown so no badge renders. */
function stepComponentLabel(component?: string): string | null {
  if (component === 'web') return 'Web';
  if (component === 'mobile') return 'Mobile';
  if (component === 'api') return 'API';
  return null;
}

/** How a step ran this time: from the replay cache, repaired by the model, or run by the model. */
type StepExecutionMode = 'replayed' | 'healed' | 'llm';

/** A step's execution mode, or undefined for anything else: the API's steps are cast, not validated. */
function stepExecutionMode(value: unknown): StepExecutionMode | undefined {
  return value === 'replayed' || value === 'healed' || value === 'llm'
    ? value
    : undefined;
}

/**
 * The report's steps with each one's execution mode copied from the matching result step: by its
 * authored index; for a step without one (the login step, older bundles), the index-less result
 * step of that name, else the only result step with that name. Exported for tests.
 */
export function withExecutionModes<
  T extends { name?: string; step_index?: number | null },
>(
  steps: T[],
  resultSteps: Record<string, unknown>[],
): (T & { executionMode?: StepExecutionMode })[] {
  return steps.map((step) => {
    const named = resultSteps.filter(
      (resultStep) => resultStep.name === step.name,
    );
    const match =
      typeof step.step_index === 'number'
        ? resultSteps.find(
            (resultStep) => resultStep.step_index === step.step_index,
          )
        : (named.find((resultStep) => resultStep.step_index == null) ??
          (named.length === 1 ? named[0] : undefined));
    const executionMode = stepExecutionMode(match?.executionMode);
    return executionMode ? { ...step, executionMode } : step;
  });
}

/**
 * One line saying how many of this run's steps came from the replay cache, and how many the model
 * repaired or ran; null when no step says how it ran. Exported for tests.
 */
export function cacheUsageLine(
  steps: { executionMode?: unknown }[],
): string | null {
  const count = (mode: StepExecutionMode) =>
    steps.filter((step) => step.executionMode === mode).length;
  const replayed = count('replayed');
  const healed = count('healed');
  const modelOnly = count('llm');
  if (replayed + healed + modelOnly === 0) return null;
  const parts = [
    `This run: ${replayed} of ${steps.length} step${steps.length === 1 ? '' : 's'} came from the replay cache`,
  ];
  if (healed)
    parts.push(
      `${healed} ${healed === 1 ? 'was' : 'were'} repaired by the model`,
    );
  if (modelOnly) parts.push(`${modelOnly} ran on the model with no recording`);
  return `${parts.join('; ')}.`;
}

function StepRow({
  runId,
  caseId,
  step,
  caseStatus,
  onSeek,
}: {
  runId: string;
  caseId: string;
  step: RenderStep;
  /** Parent case status — when terminal, an in_progress/running step is shown clamped (no spinner). */
  caseStatus?: string;
  onSeek?: StepSeekHandler;
}): JSX.Element {
  const attachments = step.attachments ?? [];
  const tc = step.timecode_ms;
  const canSeek = onSeek && typeof tc === 'number';
  const running = isLiveRunningStep(step.status, caseStatus);
  // How long the step took. Only shown for a finished step whose span is a real, whole millisecond
  // (>= 1ms) — a live/running step, a sub-1ms fraction (which formatDuration would round to "0 ms"),
  // or a run whose fragment predates per-step timing (no backfill) carries none, so the row stays as
  // before rather than showing a misleading "0 ms"/"—".
  const durationNum = Number(step.duration_ms);
  const finishedLabel =
    !running && Number.isFinite(durationNum) && durationNum >= 1
      ? formatDuration(durationNum)
      : null;
  // A running step counts up from the start the worker stamped on it, so a step stuck on a CAPTCHA
  // or a slow page reads as "3m 20s" rather than an ageless spinner. The clock only ticks while this
  // step is live, and `elapsedSinceStart` withholds a number it cannot state honestly (no start on
  // an older run, or a browser clock behind the runner's).
  // A start in the future means this browser's clock sits behind the runner's. Refusing it only for
  // as long as it stays in the future would be worse than useless: a minute later the clock catches
  // up, the value turns non-negative, and the row starts counting from 0s for a step that has been
  // running far longer. So the refusal latches for this row — once skewed, always withheld, and the
  // tick is dropped with it since nothing will be shown again.
  // Latched in an effect rather than during render: a render can be retried or discarded (React
  // StrictMode, concurrent rendering), and a side effect written there leaks out of the attempt that
  // produced it.
  const [clockSkewed, setClockSkewed] = useState(false);
  const ticking = running && isUsableStart(step.start) && !clockSkewed;
  const now = useTickingNow(ticking);
  const elapsedMs = ticking ? elapsedSinceStart(step.start, now) : null;
  useEffect(() => {
    if (ticking && elapsedMs === null) setClockSkewed(true);
  }, [ticking, elapsedMs]);
  const durationLabel =
    finishedLabel ?? (elapsedMs === null ? null : formatElapsed(elapsedMs));
  const componentLabel = stepComponentLabel(step.component);
  const ComponentIcon = platformComponentIcon(step.component);
  const showObservation =
    !!step.observation && !isReplayObservation(step.observation);
  return (
    <div className="py-1.5 pl-8">
      <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        {running ? (
          <Loader2Icon
            className="size-3 shrink-0 animate-spin text-info-500"
            aria-hidden
          />
        ) : (
          <span
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              statusDot(step.status),
            )}
          />
        )}
        <span
          className={cn(
            'min-w-0 whitespace-pre-wrap break-words',
            running && 'text-foreground',
          )}
        >
          {step.name || 'step'}
        </span>
        {step.deviation ? (
          <Badge variant="info" className="shrink-0">
            Deviation
          </Badge>
        ) : null}
        {step.executionMode === 'replayed' ? (
          <Badge
            variant="success"
            className="shrink-0"
            title="Replayed from the recorded script, with no model"
          >
            From cache
          </Badge>
        ) : step.executionMode === 'healed' ? (
          <Badge
            variant="secondary"
            className="shrink-0"
            title="The recorded script no longer matched, so the model repaired this step"
          >
            Repaired
          </Badge>
        ) : null}
        {componentLabel ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex shrink-0 items-center text-muted-foreground"
                role="img"
                aria-label={`Ran on ${componentLabel}`}
                tabIndex={0}
              >
                <ComponentIcon className="size-3.5" aria-hidden />
              </span>
            </TooltipTrigger>
            <TooltipContent>Ran on {componentLabel}</TooltipContent>
          </Tooltip>
        ) : null}
        {canSeek ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSeek!(caseId, tc!, step.component, undefined, step.video_key);
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-1.5 py-0.5 font-mono text-[0.7rem] text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
            title="Jump to this moment in the video"
          >
            <PlayCircleIcon className="size-3" aria-hidden />
            {formatTimecode(tc)}
          </button>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {durationLabel ? (
            <span className="tabular-nums text-muted-foreground/60">
              {durationLabel}
            </span>
          ) : null}
          {step.status && step.status !== 'unknown' ? (
            <span
              className={cn(
                running ? 'text-info-500' : 'text-muted-foreground/60',
              )}
            >
              {running ? 'running' : step.status}
            </span>
          ) : null}
        </div>
      </div>
      {showObservation ? (
        <p className="mt-0.5 whitespace-pre-wrap break-words pl-3.5 text-xs text-muted-foreground/80">
          {step.observation}
        </p>
      ) : null}
      {step.expected_text != null || step.actual_text != null ? (
        // Either side can arrive alone (the wire type allows each of expected_text/actual_text to
        // be independently null) — render on `!= null`, not on both being present, so a step that
        // reported only one side still shows what we do know rather than vanishing entirely. This
        // matters most on a genuine failure, which is exactly the step a reader is looking at.
        <dl className="mt-0.5 space-y-0.5 pl-3.5 text-xs">
          <div className="flex gap-1">
            <dt className="shrink-0 text-muted-foreground/70">Expected:</dt>
            <dd className="min-w-0 whitespace-pre-wrap break-words text-muted-foreground/80">
              {step.expected_text != null ? step.expected_text : 'not reported'}
            </dd>
          </div>
          <div className="flex gap-1">
            <dt className="shrink-0 text-muted-foreground/70">Actual:</dt>
            <dd
              className={cn(
                'min-w-0 whitespace-pre-wrap break-words',
                step.actual_text == null
                  ? 'text-muted-foreground/70'
                  : step.deviation
                    ? 'text-info-600'
                    : 'text-destructive',
              )}
            >
              {step.actual_text != null ? step.actual_text : 'not reported'}
            </dd>
          </div>
        </dl>
      ) : null}
      <AppExchangeEvidence exchanges={step.app_exchanges} />
      <ApiCallEvidence runId={runId} calls={step.api_calls} />
      <DocumentEvidence runId={runId} documents={step.documents} />
      {attachments.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5 pl-3.5">
          {attachments.map((a) => (
            <Screenshot key={a.key} runId={runId} attachment={a} />
          ))}
        </div>
      ) : attachments.length === 0 && step.evidence_status === 'unavailable' ? (
        <div className="mt-1.5 pl-3.5 text-xs text-muted-foreground">
          Screenshot unavailable
        </div>
      ) : null}
    </div>
  );
}

/**
 * Slack for the overflow probe, in pixels. `scrollHeight` and `clientHeight` are rounded integers of
 * subpixel values, so a clamped paragraph that happens to fit can still report a one-pixel
 * difference; requiring more than that keeps the toggle off text that is not actually cut off.
 */
const OVERFLOW_TOLERANCE_PX = 1;

/**
 * A case's failure text, clamped to two lines with a Show more disclosure.
 *
 * These strings are the agent's own narration of a failure and routinely run past 200 characters
 * ("PUT /api/forms/Form/stay-details returns HTTP 400 '…' for all valid input combinations (5+
 * attempts)…"). Left unclamped they turn a 51-case list into a wall of prose, so the row shows the
 * first two lines and the reader opens the ones they care about.
 *
 * The full text is always in the DOM — clamping is visual only, so browser search and copy still
 * find it. The toggle appears only when the text really overflows its two lines, measured after
 * layout rather than guessed from a character count, because the same string wraps differently on a
 * wide monitor and a narrow one.
 */
function ClampedFailureText({
  text,
  className,
}: {
  text: string;
  className?: string;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const textId = useId();

  /**
   * Measured in a layout effect, before paint, so the toggle never flashes in or out. Re-measured on
   * every resize of the paragraph — the same text needs the toggle on a narrow window and not on a
   * wide one, and this column's width changes with the viewport. While expanded there is no clamp to
   * overflow, so the effect neither observes nor measures then: a measurement would read "fits" and
   * take the toggle away mid-read. Collapsing re-runs the effect and re-measures. The flag is
   * therefore only refreshed in the clamped state.
   */
  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;
    if (expanded) return;
    const measure = () =>
      setOverflowing(
        element.scrollHeight > element.clientHeight + OVERFLOW_TOLERANCE_PX,
      );
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <div className={className}>
      <p
        id={textId}
        ref={textRef}
        className={cn(
          'whitespace-pre-wrap break-words',
          !expanded && 'line-clamp-2',
        )}
      >
        {text}
      </p>
      {overflowing || expanded ? (
        <button
          type="button"
          className="mt-1 font-medium underline underline-offset-2 hover:no-underline"
          aria-expanded={expanded}
          aria-controls={textId}
          aria-label={
            expanded
              ? 'Show less of the failure message'
              : 'Show more of the failure message'
          }
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((wasExpanded) => !wasExpanded);
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}

/** One attempt of a re-queued case: a header (Attempt N · status · duration) plus its own
 *  steps, error, and per-attempt video (steps carry their own timecodes into onSeek). Rendered only
 *  when a case has >1 attempt; a single attempt falls back to the flat step list. */
function AttemptBlock({
  runId,
  caseId,
  attempt,
  shownOutcome,
  onSeek,
}: {
  runId: string;
  caseId: string;
  /** The case's shown status, for the attempt the case's outcome comes from. */
  shownOutcome?: ReturnType<typeof caseDisplayStatus>;
  attempt: CaseAttempt;
  onSeek?: StepSeekHandler;
}): JSX.Element {
  const steps = attempt.steps ?? [];
  const error = attempt.error?.message ?? null;
  return (
    <div className="mt-2 first:mt-0">
      <div className="flex flex-wrap items-center gap-2 pl-8 text-xs">
        <span className="font-medium text-foreground">
          Attempt {attempt.attempt + 1}
        </span>
        <StatusBadge status={attempt.status} />
        {attempt.outcome ? (
          <StatusBadge status={shownOutcome ?? attempt.outcome} />
        ) : attempt.outcome_provenance?.source === 'legacy_status_unmapped' ? (
          <Badge variant="outline">
            Legacy evidence: {attempt.outcome_provenance.status}
          </Badge>
        ) : null}
        <span className="tabular-nums text-muted-foreground">
          {formatDuration(attempt.duration_ms)}
        </span>
      </div>
      {error ? (
        <ClampedFailureText
          text={error}
          className="ml-8 mt-1.5 rounded-md border-l-2 border-destructive bg-destructive/5 px-2 py-1.5 text-xs text-destructive"
        />
      ) : null}
      <SkillExecutionDetails evidence={attempt.skill_execution} />
      {steps.length > 0 ? (
        <div className="mt-1 space-y-0.5">
          {steps.map((step, i) => (
            <StepRow
              key={i}
              runId={runId}
              caseId={caseId}
              step={step}
              caseStatus={attempt.status}
              // Inject this attempt so its timecode seeks ITS recording (not the case-level / last
              // attempt). StepRow passes the step's component; we close over `attempt` here.
              onSeek={
                onSeek
                  ? (cid, ms, component, _stepAttempt, videoKey) =>
                      onSeek(cid, ms, component, attempt, videoKey)
                  : undefined
              }
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The steps preceding a pinned failing step (S10), collapsed behind a disclosure so the failure-first
 *  expansion doesn't bury the failure under everything that led up to it. `label` is computed by the
 *  caller — "N earlier steps" normally, "N other steps" when trailing steps also render below this
 *  control (see `CaseRow`), so the word doesn't imply it covers every non-pinned step. */
function EarlierSteps({
  label,
  steps,
  runId,
  caseId,
  caseStatus,
  onSeek,
}: {
  label: string;
  steps: RenderStep[];
  runId: string;
  caseId: string;
  caseStatus?: string;
  onSeek?: StepSeekHandler;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDownIcon className="size-3" aria-hidden />
        ) : (
          <ChevronRightIcon className="size-3" aria-hidden />
        )}
        {label}
      </button>
      {open ? (
        <div className="mt-0.5 space-y-0.5">
          {steps.map((step, i) => (
            <StepRow
              key={i}
              runId={runId}
              caseId={caseId}
              step={step}
              caseStatus={caseStatus}
              onSeek={onSeek}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The expanded body for a failure-status case with something pinned (S10, AC 24/25): the failing
 *  step itself (F23 — leads, ahead of the raw error banner, because its own Expected:/Actual: pair
 *  is the application-level statement of what went wrong; the raw framework text below it is an
 *  automation-internal detail, e.g. a Playwright locator timeout, not the diagnosis itself), then
 *  the error banner, `CachingDetail` (moved below the pinned failure, per the reconciliation with
 *  MEDA-416), then the rest of the steps. When there's a pinned step, the rest splits into
 *  `earlierSteps` (collapsed behind "N earlier/other steps") and `laterSteps` (rendered plainly
 *  below, since a step after the failure is rare and not worth hiding). An error-only pin with no
 *  failing step has nothing to split around, so the caller passes the whole list as `onlySteps`
 *  instead. The pinned step itself is never repeated in `earlierSteps`/`laterSteps`. */
function FailureFirstBody({
  runId,
  caseId,
  caseStatus,
  error,
  failingStep,
  caching,
  costRecorded,
  earlierSteps,
  laterSteps,
  onlySteps,
  onSeek,
}: {
  runId: string;
  caseId: string;
  caseStatus: string;
  error: string | null;
  failingStep?: RenderStep;
  caching: CaseReplay | null;
  costRecorded?: boolean;
  earlierSteps: RenderStep[];
  laterSteps: RenderStep[];
  onlySteps: RenderStep[];
  onSeek?: StepSeekHandler;
}): JSX.Element {
  /** "Other" rather than "earlier" once trailing steps also render below this control — they're not
   *  hidden by it, but calling the disclosure "earlier" would wrongly suggest it covers everything
   *  past the pinned step. */
  const earlierLabel = `${earlierSteps.length} ${laterSteps.length > 0 ? 'other' : 'earlier'} step${
    earlierSteps.length === 1 ? '' : 's'
  }`;
  return (
    <>
      {failingStep ? (
        <StepRow
          runId={runId}
          caseId={caseId}
          step={failingStep}
          caseStatus={caseStatus}
          onSeek={onSeek}
        />
      ) : null}
      {error ? (
        <ClampedFailureText
          text={error}
          className="mb-2 mt-1.5 rounded-md border-l-2 border-destructive bg-destructive/5 px-2 py-1.5 text-xs text-destructive"
        />
      ) : null}
      {caching ? (
        <CachingDetail
          replay={caching}
          costRecorded={costRecorded}
          usageLine={cacheUsageLine([
            ...(failingStep ? [failingStep] : []),
            ...earlierSteps,
            ...laterSteps,
            ...onlySteps,
          ])}
          omitForwardLooking
        />
      ) : null}
      {earlierSteps.length > 0 ? (
        <EarlierSteps
          label={earlierLabel}
          steps={earlierSteps}
          runId={runId}
          caseId={caseId}
          caseStatus={caseStatus}
          onSeek={onSeek}
        />
      ) : null}
      {laterSteps.length > 0 ? (
        <div className="mt-1 space-y-0.5">
          {laterSteps.map((step, i) => (
            <StepRow
              key={i}
              runId={runId}
              caseId={caseId}
              step={step}
              caseStatus={caseStatus}
              onSeek={onSeek}
            />
          ))}
        </div>
      ) : null}
      {onlySteps.length > 0 ? (
        <div className="mt-1 space-y-0.5">
          {onlySteps.map((step, i) => (
            <StepRow
              key={i}
              runId={runId}
              caseId={caseId}
              step={step}
              caseStatus={caseStatus}
              onSeek={onSeek}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

/** The kebab that carries a row's actions. The trigger is always the same affordance, so the only
 *  thing a caller varies is which items hang off it. */
function RowActionsMenu({
  result,
  children,
}: {
  result: TestResult;
  children: ReactNode;
}): JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Actions for ${caseRowLabel(result)}`}
        >
          <MoreVerticalIcon className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CorrectOutcomeItem({
  result,
  onCorrect,
}: {
  result: TestResult;
  onCorrect: (result: TestResult) => void;
}): JSX.Element {
  return (
    <DropdownMenuItem onClick={() => onCorrect(result)}>
      <PencilIcon className="size-4" aria-hidden />
      Correct outcome
    </DropdownMenuItem>
  );
}

type CorrectHandler = (
  result: TestResult,
  initialOutcome?: CorrectableOutcome,
) => void;

/**
 * Scrolls the first Requires review case's Confirm defect choice into view and focuses it, or its
 * details toggle when the viewer cannot correct the case. The target itself is scrolled, not the
 * row, so a narrow table scrolled sideways still brings the button on screen.
 */
export function revealFirstReviewCase(): void {
  const row = document.querySelector<HTMLElement>('tr[data-requires-review]');
  if (!row) return;
  const target =
    row.querySelector<HTMLElement>('[data-review-choice="defect_found"]') ??
    row.querySelector<HTMLElement>('button[aria-expanded]') ??
    row;
  if (target === row) row.tabIndex = -1;
  target.scrollIntoView({ block: 'center', inline: 'nearest' });
  target.focus({ preventScroll: true });
}

/**
 * The R13 choice on a Requires review case: confirm the defect, or say the case needs to be clearer.
 * The note renders at once; the buttons join it when the viewer can correct the case, so the row
 * does not move when the report bundle that enables correction arrives.
 */
function RequiresReviewChoice({
  result,
  onCorrect,
}: {
  result: TestResult;
  onCorrect?: CorrectHandler;
}): JSX.Element {
  const confidence = result.classification?.judge?.confidence;
  return (
    <div
      className="mt-2 flex flex-col gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <p className="text-foreground">
        The AI judge could not confirm this result. Look at the steps and video,
        then choose.
      </p>
      {typeof confidence === 'number' && Number.isFinite(confidence) ? (
        <p className="text-muted-foreground">
          Judge confidence: {Math.round(confidence * 100)}%
        </p>
      ) : null}
      {onCorrect ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            data-review-choice="defect_found"
            onClick={() => onCorrect(result, 'defect_found')}
          >
            Confirm defect
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCorrect(result, 'case_unclear')}
          >
            Not a defect
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The actions for a row that can carry an AI analysis. This is the ONLY place the reassessment
 * query mounts, which is what keeps a report of rows that can never be analyzed free of both the
 * requests and the query client. Analyze and the audit trail are separate entries on purpose: an
 * applied analysis can move the case to an outcome that is no longer reassessable, and the trail of
 * that analysis has to stay reachable afterwards.
 */
function ReassessableRowActions({
  runId,
  result,
  onCorrect,
}: {
  runId: string;
  result: TestResult;
  onCorrect?: (result: TestResult) => void;
}): JSX.Element | null {
  const reassessment = useOutcomeReassessment({
    runId,
    result,
    enabled: true,
  });
  const [detailOpen, setDetailOpen] = useState(false);
  /** A candidate the service currently refuses — a run still executing, a manual correction it may
   *  not replace — used to render a disabled button and its reason. Losing the menu entry would
   *  leave the reviewer with no way to find out why nothing is on offer, so the entry stays and the
   *  dialog carries the reason. */
  const blocked =
    !reassessment.canAnalyze &&
    !reassessment.hasHistory &&
    reassessment.eligibility !== null &&
    !reassessment.eligibility.eligible;
  if (
    !onCorrect &&
    !reassessment.canAnalyze &&
    !reassessment.hasHistory &&
    !blocked
  )
    return null;
  return (
    <>
      <RowActionsMenu result={result}>
        {reassessment.canAnalyze ? (
          <DropdownMenuItem
            disabled={reassessment.analyzing || reassessment.pending}
            onClick={reassessment.analyze}
          >
            <SparklesIcon className="size-4" aria-hidden />
            {reassessment.analyzing
              ? 'Reassessing outcome with AI…'
              : reassessment.hasHistory
                ? 'Reassess outcome with AI again'
                : 'Reassess outcome with AI'}
          </DropdownMenuItem>
        ) : null}
        {reassessment.hasHistory ? (
          <DropdownMenuItem onClick={() => setDetailOpen(true)}>
            <SparklesIcon className="size-4" aria-hidden />
            View reassessment history
          </DropdownMenuItem>
        ) : blocked ? (
          <DropdownMenuItem onClick={() => setDetailOpen(true)}>
            <SparklesIcon className="size-4" aria-hidden />
            Why outcome reassessment is unavailable
          </DropdownMenuItem>
        ) : null}
        {onCorrect ? (
          <CorrectOutcomeItem result={result} onCorrect={onCorrect} />
        ) : null}
      </RowActionsMenu>
      <OutcomeReassessmentDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        caseTitle={caseRowLabel(result)}
        state={reassessment}
      />
    </>
  );
}

/** A repeated run's case (MEDA-1288): the source case it repeats and its iteration number. */
function iterationOf(
  result: TestResult,
): { sourceCaseId: string; iteration: number } | null {
  if (!result.spec || typeof result.spec !== 'object') return null;
  const { source_case_id: sourceCaseId, iteration } = result.spec as Record<
    string,
    unknown
  >;
  return typeof sourceCaseId === 'string' && typeof iteration === 'number'
    ? { sourceCaseId, iteration }
    : null;
}

/** The row's name. An iteration reads by its number: its group header already carries the title. */
function caseRowLabel(result: TestResult): string {
  const iteration = iterationOf(result);
  return iteration
    ? `Iteration ${iteration.iteration}`
    : result.title || result.case_id;
}

/** Statuses that are not a failed iteration: passed, skipped, or not finished yet. */
const NOT_FAILED_ITERATION = new Set([
  'passed',
  'not_run',
  'queued',
  'running',
  'pending',
]);

/**
 * The group header counter, such as "11/30 passed · stopped at #12". A stopped group names its own
 * first failed iteration. When another case's failure stopped it, it names the last iteration that
 * ran instead: "stopped after #11".
 */
export function iterationGroupSummary(children: TestResult[]): string {
  const rows = children.map((child) => ({
    iteration: iterationOf(child)?.iteration ?? 0,
    status: caseDisplayStatus(child),
  }));
  const passed = rows.filter((row) => row.status === 'passed').length;
  const counter = `${passed}/${children.length} passed`;
  if (!rows.some((row) => row.status === 'not_run')) return counter;
  const failed = rows
    .filter((row) => !NOT_FAILED_ITERATION.has(row.status))
    .map((row) => row.iteration);
  if (failed.length > 0)
    return `${counter} · stopped at #${Math.min(...failed)}`;
  const ran = rows
    .filter((row) => row.status !== 'not_run')
    .map((row) => row.iteration);
  return ran.length > 0
    ? `${counter} · stopped after #${Math.max(...ran)}`
    : counter;
}

/**
 * The report's rows in order: an ordinary case stays one row, and every iteration of a source case
 * joins one group at the position of its first iteration, sorted by iteration number.
 */
function groupIterations(
  results: TestResult[],
): Array<
  { result: TestResult } | { sourceCaseId: string; children: TestResult[] }
> {
  const entries: Array<
    { result: TestResult } | { sourceCaseId: string; children: TestResult[] }
  > = [];
  const groups = new Map<string, TestResult[]>();
  for (const result of results) {
    const sourceCaseId = iterationOf(result)?.sourceCaseId;
    if (sourceCaseId === undefined) {
      entries.push({ result });
      continue;
    }
    const children = groups.get(sourceCaseId) ?? [];
    if (!groups.has(sourceCaseId)) {
      groups.set(sourceCaseId, children);
      entries.push({ sourceCaseId, children });
    }
    children.push(result);
  }
  for (const children of groups.values())
    children.sort(
      (left, right) =>
        (iterationOf(left)?.iteration ?? 0) -
        (iterationOf(right)?.iteration ?? 0),
    );
  return entries;
}

/** The header row of one source case's iterations: its title and the pass counter. */
function IterationGroupHeader({
  sourceCaseId,
  iterations,
}: {
  sourceCaseId: string;
  iterations: TestResult[];
}): JSX.Element {
  const title = iterations[0]?.title || sourceCaseId;
  return (
    <tr className="bg-muted/20">
      <td colSpan={5} className="px-3 py-2">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <span className="min-w-0 truncate font-medium" title={title}>
            {title}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {iterationGroupSummary(iterations)}
          </span>
        </div>
      </td>
    </tr>
  );
}

function CaseRow({
  runId,
  result,
  detail,
  reapedReason,
  onSeek,
  suiteRunId,
  suiteProjectId,
  suiteSource,
  terminal = false,
  expandCaseId,
  onCorrect,
  onCancel,
  cancelling,
}: {
  runId: string;
  result: TestResult;
  detail?: CaseDetail;
  /** Run-level reaped reason — used to derive a per-case reason for a terminated case whose row has
   *  no own `reason_code` (surfaces a reason on runs predating the column, no backfill). */
  reapedReason?: ReapedReason | null;
  onSeek?: StepSeekHandler;
  /** When set, the row links into the suite with this case opened (the suite-detail route id).
   *  `suiteSource` picks the provider so a Zephyr cycle opens under Zephyr, not TestRail. */
  suiteRunId?: string;
  suiteProjectId?: string;
  suiteSource?: TestSource;
  /** Whole-run terminal state (S10, AC 24/25) — gates auto-expansion. Defaults to `false` so a caller
   *  that doesn't pass it (a run still in flight, or an existing test) never auto-expands. */
  terminal?: boolean;
  /** A deep link's target case (S11, AC 26/27) — expands this row once, the same way `autoExpand`
   *  does, and is likewise overridden by a manual collapse (`touchedRef`). */
  expandCaseId?: string;
  onCorrect?: CorrectHandler;
  onCancel?: (caseId: string) => void;
  cancelling?: boolean;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  /** Set the moment the user opens/closes the row by hand, so the auto-expand effect below never
   *  overrides a deliberate collapse (AC 25) — including one made before the run (and thus this
   *  case's own auto-expand eligibility) went terminal. */
  const touchedRef = useRef(false);

  // Prefer the report's enriched steps (observation + attachments + timecodes) once available; fall
  // back to the live steps streamed during the run.
  // This projection is why a live step never showed a duration: it kept name and status and dropped
  // everything else the worker streams. `start` (epoch ms, stamped when the step began) drives the
  // running step's live elapsed time, and `duration_ms` lets an already-finished live step show its
  // own measured span before the enriched report fragment arrives.
  // `Array.isArray` rather than trusting the declared type: these steps are a jsonb column handed
  // back as-is, so a partial payload or an API regression reaches `.map()` as whatever it is, and
  // rendering the run detail is not the place to throw.
  const liveSteps: Record<string, unknown>[] = Array.isArray(result.steps)
    ? (result.steps as unknown as Record<string, unknown>[])
    : [];
  const baseSteps: RenderStep[] = Array.isArray(detail?.steps)
    ? withExecutionModes(detail.steps, liveSteps)
    : liveSteps.map((s) => ({
        name: typeof s.name === 'string' ? s.name : '',
        status: typeof s.status === 'string' ? s.status : 'unknown',
        step_index: typeof s.step_index === 'number' ? s.step_index : null,
        executionMode: stepExecutionMode(s.executionMode),
        observation:
          typeof s.observation === 'string' ? s.observation : undefined,
        duration_ms: asNumberOrString(s.duration_ms),
        start: asNumberOrString(s.start),
        expected_text:
          typeof s.expected_text === 'string' ? s.expected_text : undefined,
        actual_text:
          typeof s.actual_text === 'string' ? s.actual_text : undefined,
        deviation: s.deviation === true,
      }));
  const steps: RenderStep[] = baseSteps;
  const hasSteps = steps.length > 0;
  const error = detail?.error?.message ?? null;
  // Per-attempt history (schema 3): render the attempts panel only when a case was actually retried
  // (>1 attempt). A single attempt — or a schema ≤2 report with no `attempts` — renders flat as before.
  /** Only the attempt this result is for has execution modes; earlier attempts stay unmarked. */
  const attempts = Array.isArray(detail?.attempts)
    ? detail.attempts.map((attempt) =>
        attempt.attempt === result.attempt && Array.isArray(attempt.steps)
          ? { ...attempt, steps: withExecutionModes(attempt.steps, liveSteps) }
          : attempt,
      )
    : undefined;
  const multiAttempt = !!attempts && attempts.length > 1;
  const caching = hasCachingInfo(result.replay) ? result.replay : null;
  const costRecorded = detail?.cost_recorded ?? result.cost_recorded;
  const history = detail?.history ?? result.history;
  const savedVariables = capturedVariableEntries(result.captured_variables);
  const answerJudgement = readAnswerJudgement(result.answer_judgement);
  const expandable =
    hasSteps ||
    !!error ||
    multiAttempt ||
    !!caching ||
    !!history ||
    savedVariables.length > 0 ||
    answerJudgement !== null;
  const requiresReview = requiresOutcomeReview(result);
  const displayStatus = caseDisplayStatus(result);
  /** Null for a legacy `ResultStatus` (`failed`/`broken`/…) — those have no outcome tooltip of
   *  their own, so the badge renders exactly as before a canonical run makes `displayStatus` a
   *  real `CaseOutcome`. */
  /** Ticks only while Assessing, so the tooltip turns to "Taking longer" once `ready_by` passes. */
  const assessingNow = useTickingNow(displayStatus === 'assessing');
  const displayStatusTooltip =
    displayStatus === 'assessing'
      ? assessingTooltip(result.assessment, assessingNow)
      : outcomeTooltip(displayStatus);
  const caseStatus =
    result.outcome === 'queued' || result.outcome === 'running'
      ? result.outcome
      : (caseOutcome(result) ?? result.status);

  /** Failure-first expansion (S10, AC 24/25). `expandable` widened for caching (`|| !!caching`
   *  above); this stays independent of it on purpose — a passing case carrying caching info is
   *  expandable by click but must never auto-expand. */
  const isFailureStatus = FAILURE_STATUSES.has(caseStatus);
  const pinnedStepIndex = isFailureStatus ? findPinnedStepIndex(steps) : -1;
  const failingStep = pinnedStepIndex >= 0 ? steps[pinnedStepIndex] : undefined;
  /** Something worth pinning: an error message, or a non-passed step. A failure-status case with
   *  neither (every step passed, no error — the `reason` line covers it inline) stays collapsed and
   *  renders like any other case; it does not auto-expand into an empty-looking body. */
  const hasSomethingToPin = !!error || !!failingStep;
  /** Multi-attempt cases keep their existing per-attempt layout (each AttemptBlock already leads
   *  with its own error) — the pinned-block layout only applies to the flat, single-attempt case. */
  const failureFirst = isFailureStatus && hasSomethingToPin && !multiAttempt;
  const autoExpand = terminal && isFailureStatus && hasSomethingToPin;

  // Live progress: while the case is running, surface the step currently executing as "step N/M: name"
  // in the row subtitle (visible without expanding). N counts real steps reached (preconditions, named
  // "Precondition: …", are excluded); M is the planned step count from the case spec when known.
  const running = caseStatus === 'running';
  const current = running ? liveCurrentStep(steps, result.spec) : null;
  // Before the step phase, the case spends its time in preconditions (login + sync on mobile, heavy
  // TestRail setup on web). Surface the precondition currently being worked on so the running row
  // isn't blank for minutes; its name already carries the "Precondition: …" prefix.
  const currentPre =
    running && !current
      ? steps.find((s) => isPreconditionStep(s.name) && isRunningStep(s.status))
      : undefined;

  // WHY the case ended without a normal verdict (broken/failed/timeout/cancelled with no own error).
  // Shown inline in the row so a no-steps terminal case — which isn't expandable — is never left blank.
  const reason = !running ? caseReasonText(result, reapedReason) : null;

  /** WHAT the outcome means for a case that did produce a verdict (R3) — the judge's own
   *  explanation, or the failing step's observation when no judge ran. `failingStep` is only
   *  computed above for a failure-status case, which is exactly the set this line is for. */
  const rationale = caseRationale(result, failingStep);

  // Auto-expand the running case once, so its steps are visible as they tick in. Fires only on the
  // queued→running edge (dep is the boolean), so a manual collapse mid-run is not overridden.
  useEffect(() => {
    if (running) setExpanded(true);
  }, [running]);

  /** Auto-expand a terminal failure once, so the pinned error/step is visible without a click
   *  (AC 24). Fires on the `autoExpand` false→true edge (including the initial render of an
   *  already-terminal page load) and is skipped once the user has touched the row by hand (AC 25) —
   *  a manual collapse must never be reopened by the run finishing underneath it. */
  useEffect(() => {
    if (autoExpand && !touchedRef.current) setExpanded(true);
  }, [autoExpand]);

  /** Deep-link restore (S11, AC 26/27): expand this row once it's named as the link's target case
   *  — fires on the id-match edge, same as the effects above, and never fights a manual collapse. */
  useEffect(() => {
    if (expandCaseId && result.case_id === expandCaseId && !touchedRef.current)
      setExpanded(true);
  }, [expandCaseId, result.case_id]);

  const toggleExpanded = (): void => {
    touchedRef.current = true;
    setExpanded((v) => !v);
  };

  /** Case ids are TestRail/run-scoped slugs (e.g. "C1") — safe to use verbatim in an id attribute,
   *  and stable enough to link the disclosure row to the body it controls (Copilot round 5). */
  const detailId = `case-detail-${result.case_id}`;

  /** Coerced once here rather than re-derived at each of the badge's four usage sites below. */
  const deviations = deviationCount(result.deviation_count);
  const canonicalOutcome = caseOutcome(result);

  /** Only rows that can actually carry an analysis mount the reassessment query. A report of a
   *  hundred passing cases would otherwise issue a hundred requests on open; a canonical row that is
   *  not a candidate earns its probe once the reviewer expands it. */
  const reassessmentVisible =
    isOutcomeReassessmentCandidate(result) ||
    hasAppliedOutcomeReassessment(result) ||
    (expanded && canonicalOutcome !== null);
  const correct =
    canonicalOutcome !== null && onCorrect !== undefined
      ? onCorrect
      : undefined;

  return (
    <>
      <tr
        className={cn('hover:bg-muted/30', expandable && 'cursor-pointer')}
        onClick={() => expandable && toggleExpanded()}
        data-requires-review={displayStatus === 'unresolved' ? '' : undefined}
      >
        <td className="px-3 py-2.5 align-top">
          {expandable ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-m-1 size-6 rounded-sm text-muted-foreground"
              aria-label={`Show details for ${caseRowLabel(result)}`}
              aria-expanded={expanded}
              aria-controls={detailId}
              onClick={(event) => {
                event.stopPropagation();
                toggleExpanded();
              }}
            >
              {expanded ? (
                <ChevronDownIcon className="size-4" aria-hidden />
              ) : (
                <ChevronRightIcon className="size-4" aria-hidden />
              )}
            </Button>
          ) : null}
        </td>
        <td className="px-3 py-2.5">
          {/* min-w-0 lets the truncating title shrink: a flex child's floor is its min-content
              width, so without it the title pushes the row wide instead of ellipsing. */}
          <div className="flex min-w-0 items-center gap-1.5">
            <span
              className="min-w-0 truncate font-medium"
              title={caseRowLabel(result)}
            >
              {caseRowLabel(result)}
            </span>
            {suiteRunId ? (
              <Link
                to="/quality/suites/$id"
                params={{ id: suiteRunId }}
                search={{
                  projectId: suiteProjectId,
                  caseId: iterationOf(result)?.sourceCaseId ?? result.case_id,
                  source: suiteSource,
                }}
                // Don't toggle the row when following the link (click, or Enter/Space on the
                // focused link — both otherwise bubble to the row's keyboard expand handler).
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                }}
                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                title="Open this case in its suite"
                aria-label="Open this case in its suite"
              >
                <ExternalLinkIcon className="size-3.5" aria-hidden />
              </Link>
            ) : null}
          </div>
          {current || currentPre ? (
            <div className="flex min-w-0 items-center gap-1.5 text-xs text-info-500">
              <Loader2Icon
                className="size-3 shrink-0 animate-spin"
                aria-hidden
              />
              {/* min-w-0 on the span too, not only on the row: a nowrap flex item's floor is its
                  own min-content width, so without it the ellipsis never appears. */}
              <span className="min-w-0 truncate">
                {current
                  ? `step ${current.n}${current.total ? `/${current.total}` : ''}: ${current.name}`
                  : currentPre!.name}
              </span>
            </div>
          ) : (
            <div className="truncate text-xs text-muted-foreground">
              {result.case_id}
              {multiAttempt
                ? ` · ${attempts!.length} attempts`
                : hasSteps
                  ? ` · ${steps.length} step${steps.length === 1 ? '' : 's'}`
                  : ''}
            </div>
          )}
          {!multiAttempt ? (
            <SkillExecutionDetails
              evidence={
                detail?.skill_execution ?? attempts?.[0]?.skill_execution
              }
            />
          ) : null}
          {reason ? (
            <p
              className={cn(
                'mt-1 break-words text-xs',
                displayStatus === 'not_run'
                  ? 'text-muted-foreground'
                  : 'text-warning-600',
              )}
            >
              {reason}
            </p>
          ) : null}
          {rationale ? (
            <div className="mt-1 flex gap-1.5 text-xs text-muted-foreground">
              <span className="shrink-0 font-medium text-foreground">
                {rationale.fromJudge ? 'Why:' : 'What happened:'}
              </span>
              <ClampedFailureText text={rationale.text} className="min-w-0" />
            </div>
          ) : displayStatus === 'defect_found' && !reason ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Explanation unavailable.
            </p>
          ) : null}
          {displayStatus === 'unresolved' ? (
            <RequiresReviewChoice result={result} onCorrect={correct} />
          ) : null}
          {/* Farm device evidence (AC6): which physical device the shard actually ran on, confirmed
              against driver.caps by the worker, whether it paid the app sync cost, and how long
              that sync took — the number a human actually compares across shards to confirm lane
              affinity is working. `device_udid` comes from a confirmed farm pin, while the sync
              fields come from the mobile sync guard independently of it, so a BrowserStack case or
              an unconfirmed farm pin can carry sync evidence with no udid — render on any of the
              three rather than gating all of them on the udid. Absent on non-farm runs and on
              results older than this feature — the row renders exactly as before when none of
              these fields are set. */}
          {result.device_udid ||
          result.sync_state ||
          result.sync_duration_ms != null ? (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              {result.device_udid ? (
                <span title={result.device_udid}>
                  {truncateUdid(result.device_udid)}
                </span>
              ) : null}
              {result.sync_state ? (
                <Badge
                  variant={result.sync_state === 'cold' ? 'warning' : 'success'}
                >
                  {result.sync_state}
                </Badge>
              ) : null}
              {result.sync_duration_ms != null ? (
                <span>{formatDuration(result.sync_duration_ms)}</span>
              ) : null}
            </div>
          ) : null}
        </td>
        <td className="px-3 py-2.5">
          {/* flex-col, not a single row: the deviation badge's "N deviations" text is wider than
              a StatusBadge, and stacking keeps both inside this fixed-width column instead of
              spilling past it into the Duration column (a single nowrap row did exactly that). */}
          <div className="flex flex-col items-start gap-1">
            <div className="flex max-w-full flex-wrap items-center gap-1">
              {displayStatusTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* `StatusBadge` doesn't forward arbitrary props/ref, so the trigger wraps it in a
                        plain tabIndex-able span rather than putting `asChild` directly on the badge —
                        same reason `CancelReasonTooltip` in quality-presentation.tsx does this. */}
                    <span tabIndex={0} className="inline-flex">
                      <StatusBadge status={displayStatus} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{displayStatusTooltip}</TooltipContent>
                </Tooltip>
              ) : (
                <StatusBadge status={displayStatus} />
              )}
              {result.needs_triage && !requiresReview ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      role="img"
                      aria-label="Needs review"
                      className="inline-flex shrink-0 rounded-sm text-warning-600 outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <CircleAlertIcon className="size-3.5" aria-hidden />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {triageTooltip(result.triage_reason)}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>
            {result.classification?.source === 'manual' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline">Manually corrected</Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Corrected by{' '}
                    {result.classification.manual?.actor_name ?? 'Unknown user'}
                    {result.classification.manual?.applied_at
                      ? ` · ${formatDate(result.classification.manual.applied_at)}`
                      : ''}
                  </p>
                  {result.classification.manual?.comment ? (
                    <p>{result.classification.manual.comment}</p>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            ) : null}
            {deviations > 0 ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="info">
                    {deviations} deviation
                    {deviations === 1 ? '' : 's'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {deviations === 1
                    ? 'One step passed despite a cosmetic mismatch — see its expected vs. actual text below.'
                    : 'These steps passed despite a cosmetic mismatch — see each expected vs. actual text below.'}
                </TooltipContent>
              </Tooltip>
            ) : null}
            <AnswerJudgementBadges judgement={answerJudgement} />
          </div>
        </td>
        <td className="px-3 py-2.5 text-xs tabular-nums text-muted-foreground">
          <div>{formatDuration(result.duration_ms)}</div>
        </td>
        {/* The row's own actions live here, not in the Status cell. Status answers "what is this
            case now"; everything a reviewer can do to the case hangs off one menu, so the column
            stays readable and a new action costs no extra width. */}
        <td
          className="px-2 py-2.5 align-top"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {onCancel && ['queued', 'pending', 'running'].includes(caseStatus) ? (
            <RowActionsMenu result={result}>
              <DropdownMenuItem
                disabled={cancelling}
                onClick={() => onCancel(result.case_id)}
              >
                Cancel
              </DropdownMenuItem>
            </RowActionsMenu>
          ) : reassessmentVisible ? (
            <ReassessableRowActions
              runId={runId}
              result={result}
              onCorrect={correct}
            />
          ) : correct ? (
            <RowActionsMenu result={result}>
              <CorrectOutcomeItem result={result} onCorrect={correct} />
            </RowActionsMenu>
          ) : null}
        </td>
      </tr>
      {expanded && expandable ? (
        <tr id={detailId} className="bg-muted/10">
          <td />
          <td className="px-3 pb-3" colSpan={4}>
            {failureFirst ? (
              <FailureFirstBody
                runId={runId}
                caseId={result.case_id}
                caseStatus={caseStatus}
                error={error}
                failingStep={failingStep}
                caching={caching}
                costRecorded={costRecorded}
                earlierSteps={
                  pinnedStepIndex > 0 ? steps.slice(0, pinnedStepIndex) : []
                }
                laterSteps={
                  pinnedStepIndex >= 0 ? steps.slice(pinnedStepIndex + 1) : []
                }
                onlySteps={pinnedStepIndex < 0 ? steps : []}
                onSeek={onSeek}
              />
            ) : (
              <>
                {caching ? (
                  <CachingDetail
                    replay={caching}
                    costRecorded={costRecorded}
                    usageLine={cacheUsageLine(steps)}
                  />
                ) : null}
                {history ? (
                  <div className="mb-3 rounded-md border border-warning-300 bg-warning-50 p-2 text-xs text-warning-800">
                    <p>Legacy original status: {history.original_status}</p>
                    {Array.isArray(history.reasons) &&
                    history.reasons.length ? (
                      <p>Mapping notes: {history.reasons.join(', ')}</p>
                    ) : null}
                  </div>
                ) : null}
                {multiAttempt ? (
                  <div className="space-y-3">
                    {attempts!.map((a) => (
                      <AttemptBlock
                        key={a.attempt}
                        runId={runId}
                        caseId={result.case_id}
                        attempt={a}
                        shownOutcome={
                          a.attempt === result.attempt
                            ? displayStatus
                            : undefined
                        }
                        onSeek={onSeek}
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    {error ? (
                      <p className="mb-2 rounded-md border-l-2 border-destructive bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
                        {error}
                      </p>
                    ) : null}
                    {hasSteps ? (
                      <div className="space-y-0.5">
                        {steps.map((step, i) => (
                          <StepRow
                            key={i}
                            runId={runId}
                            caseId={result.case_id}
                            step={step}
                            caseStatus={caseStatus}
                            onSeek={onSeek}
                          />
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </>
            )}
            <AnswerJudgementDetails judgement={answerJudgement} />
            <CapturedVariables entries={savedVariables} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function CasesReport({
  runId,
  results,
  reportCases,
  reapedReason,
  onSeek,
  suiteRunId,
  suiteProjectId,
  suiteSource,
  terminal,
  expandCaseId,
  note,
  onCorrect,
  onCancel,
  cancelling,
}: {
  runId: string;
  results: TestResult[];
  reportCases?: CaseDetail[];
  /** Run-level reaped reason, threaded to each row so a terminated case with no own `reason_code`
   *  derives its reason from it (the #39 shape — no backfill needed). */
  reapedReason?: ReapedReason | null;
  /** Jump the Video tab to a step's moment. `component` (the step's platform) targets that component's
   *  recording, falling back to the run's platform; `attempt` (set by the per-attempt block) targets
   *  that attempt's recording, omitted → the case-level (last-attempt) video. Present only when the
   *  run has video. */
  onSeek?: StepSeekHandler;
  /** Suite-detail route id + project scope; when set, each case row links into the suite with itself
   *  opened. Absent for runs with no suite-detail page (folder/inline/mock). `suiteSource` picks the
   *  provider (TestRail run vs Zephyr cycle) — the id alone is ambiguous across sources. */
  suiteRunId?: string;
  suiteProjectId?: string;
  suiteSource?: TestSource;
  /** Whole-run terminal state (S10, AC 24/25) — gates failure-first auto-expansion. Omitted (rather
   *  than defaulted here) so every existing caller/test that doesn't pass it keeps today's behaviour
   *  (nothing auto-expands from a terminal-status case alone); `CaseRow` defaults it to `false`. */
  terminal?: boolean;
  /** A deep link's target case (S11, AC 26/27) — the matching row expands once on restore. */
  expandCaseId?: string;
  /** A run-level note about these cases (today: the slowest-steps summary), rendered between the
   *  card header and the table so it reads as part of the table rather than as a banner the user
   *  must get past to reach it. */
  note?: ReactNode;
  /** Open a manual correction for the current canonical outcome, optionally prefilled. */
  onCorrect?: CorrectHandler;
  onCancel?: (caseId: string) => void;
  cancelling?: boolean;
}): JSX.Element {
  const byId = new Map((reportCases ?? []).map((c) => [c.id, c]));
  const renderRow = (r: TestResult): JSX.Element => (
    <CaseRow
      key={r.case_id}
      runId={runId}
      result={r}
      detail={byId.get(r.case_id)}
      reapedReason={reapedReason}
      onSeek={onSeek}
      suiteRunId={suiteRunId}
      suiteProjectId={suiteProjectId}
      suiteSource={suiteSource}
      terminal={terminal}
      expandCaseId={expandCaseId}
      onCorrect={onCorrect}
      onCancel={onCancel}
      cancelling={cancelling}
    />
  );

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Cases
        </h2>
        <span className="text-xs tabular-nums text-muted-foreground">
          {results.length}
        </span>
      </div>
      {note}
      {results.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          No cases yet. Cases appear as workers pick them up.
        </p>
      ) : (
        <div className="overflow-x-auto">
          {/* table-fixed is what keeps this list inside the card. With the default auto layout a
              cell's min-content width wins, and every nowrap cell here (the truncated case title,
              the live step line) is as wide as its full text — so one long title dragged the table
              past the viewport, pushed Status and Duration out of sight, and let a failure string
              lay itself out on a single 2000px line. Fixed layout gives the columns their declared
              widths and makes Case absorb the rest, so long text wraps or truncates in place. On a
              phone the fixed columns alone are wider than the card, so the table keeps a minimum
              width and scrolls sideways instead of printing Case under Status. */}
          <table className="w-full min-w-[30rem] table-fixed text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-8 px-3 py-2.5 font-medium" />
                <th className="px-3 py-2.5 font-medium">Case</th>
                <th className="w-32 px-3 py-2.5 font-medium sm:w-36">Status</th>
                <th className="w-24 px-3 py-2.5 font-medium sm:w-40">
                  Duration
                </th>
                <th className="w-12 px-2 py-2.5 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {groupIterations(results).map((entry) =>
                'result' in entry ? (
                  renderRow(entry.result)
                ) : (
                  <Fragment key={`group:${entry.sourceCaseId}`}>
                    <IterationGroupHeader
                      sourceCaseId={entry.sourceCaseId}
                      iterations={entry.children}
                    />
                    {entry.children.map(renderRow)}
                  </Fragment>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
